import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { basePageWrap } from '../BasePage';
import styles from './ObjectPage.module.scss';

import i18n from '../../i18n';
import { useTranslation } from 'next-i18next';
import ArticleEntry from '../../components/ArticleEntry';
import RichText from '../../components/RichText';
import InfoBox from '../../components/InfoBox';
import Picture from '../../components/Picture';
import GuideList from '../../components/GuideList';
import GuideTeaser from '../../components/GuideTeaser';
import MassInfo from '../../components/MassInfo';
import LabeledIcon from '../../components/LabeledIcon';
import Button from '../../components/Button';
import ShareModal from '../../components/ShareModal';
import {
    isPageIdSaved,
    saveOrRemoveId,
    MY_LIST_KEY_OBJECTS,
    MY_LIST_KEY_GUIDES,
} from '../../utils/MyListStorage';
import ObjectEntry from '../../components/ObjectEntry';

const ObjectPage = ({
    featuredImage,
    articleEntry,
    richText,
    infoBox,
    infoBoxText,
    featuredGuides = [],
    relatedGuides = [],
    massInfo = [],
    relatedEventsList = [],
    relatedEventsTitle,
}) => {
    const { t } = useTranslation();
    const [modalIsOpen, setModalOpen] = useState(false);
    const [isSaved, setSavedStatus] = useState();

    const { id, pageId, title, categories, pageType } = articleEntry;
    const storageKey =
        pageType === 'GuidePage' ? MY_LIST_KEY_GUIDES : MY_LIST_KEY_OBJECTS;

    useEffect(() => {
        if (pageId) {
            setSavedStatus(isPageIdSaved(storageKey, pageId));
        }
    }, [pageId, storageKey]);

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleSaveClick = () => {
        if (!pageId) return;
        let isSaved = saveOrRemoveId(storageKey, pageId, title, categories);
        setSavedStatus(isSaved);
    };

    const saveButtonText = t(isSaved ? 'generic.saved' : 'generic.save');

    console.log('infobox', infoBox);
    console.log('infoboxtext', infoBoxText);

    return (
        <section className={styles['ObjectPage']}>
            <div
                className={
                    styles['ObjectPage__Main'] +
                    ' ' +
                    styles['ObjectPage__Main--Grid']
                }>
                <section className={styles['ObjectPage__Section']}>
                    <ArticleEntry
                        {...articleEntry}
                        smallFonts={true}
                        displayActions={false}
                    />
                </section>

                <section className={styles['ObjectPage__AsideWrapper']}>
                    <div className={styles['ObjectPage__Content']}>
                        {featuredImage && (
                            <div
                                className={styles['ObjectPage__FeaturedImage']}>
                                <Picture
                                    className={
                                        styles['ObjectPage__FeaturedImage']
                                    }
                                    image={featuredImage}
                                    size="large"
                                    classNameBorderRadius={
                                        styles['ObjectPage__NoBorderRadius']
                                    }
                                    lazy={false}
                                />
                                {featuredImage.caption && (
                                    <figcaption
                                        className={
                                            styles[
                                                'ObjectPage__FeaturedImage__Caption'
                                            ]
                                        }>
                                        {featuredImage.caption}
                                    </figcaption>
                                )}
                            </div>
                        )}

                        {massInfo.map((info, i) => (
                            <MassInfo key={i} {...info} />
                        ))}
                        <RichText {...richText} />

                        <div className={styles['ObjectPage__Section']}>
                            {infoBox && (
                                <InfoBox
                                    {...infoBox}
                                    modifiers={['Gray', 'PinkBorder']}
                                    heading={t('EventPageContent.gettingThere')}
                                />
                            )}
                            {infoBoxText && (
                                <InfoBox
                                    {...infoBoxText}
                                    modifiers={['Gray', 'PinkBorder']}
                                />
                            )}
                        </div>

                        {!!relatedEventsList.length && (
                            <div
                                className={styles['ObjectPage__RelatedEvents']}>
                                <h2
                                    className={
                                        styles['ObjectPage__RelatedEventsTitle']
                                    }>
                                    {relatedEventsTitle ||
                                        t('Event.relatedEvents')}
                                </h2>
                                <div
                                    className={
                                        styles['ObjectPage__RelatedEventsList']
                                    }>
                                    {relatedEventsList.map((event, index) => (
                                        <ObjectEntry
                                            key={index}
                                            {...event}
                                            isGrey
                                            isCompact={true}
                                            hideCursor={true}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className={styles['ObjectPage__MetaContainer']}>
                            <div
                                className={
                                    styles['ObjectPage__LabeledIconWrapper']
                                }>
                                <LabeledIcon
                                    type="share"
                                    onClick={handleOpenModal}
                                    label={t('generic.share')}
                                    color="black800"
                                    isVertical={true}
                                />
                            </div>

                            <Button
                                type="secondary"
                                onClick={handleSaveClick}
                                isClicked={isSaved}
                                icon="bookmark"
                                iconColor="black800"
                                iconSize="medium"
                                text={saveButtonText}
                            />
                        </div>
                    </div>
                    <aside className={styles['ObjectPage__Aside']}>
                        {!!relatedGuides.length && (
                            <p className={styles['ObjectPage__AsideTitle']}>
                                {t('Guides.relatedGuideTeaser')}
                            </p>
                        )}
                        <div className={styles['ObjectPage__GuideTeasers']}>
                            {relatedGuides.map((guide, index) => (
                                <div
                                    key={index}
                                    className={
                                        styles['ObjectPage__GuideTeaserWrapper']
                                    }>
                                    <GuideTeaser {...guide} />
                                </div>
                            ))}
                        </div>
                    </aside>
                </section>
            </div>

            {!!featuredGuides.length && (
                <div className={styles['ObjectPage__FeaturedGuides']}>
                    <GuideList
                        items={featuredGuides}
                        title={t('Object.relatedGuides')}
                    />
                </div>
            )}

            <ShareModal
                title={title}
                isOpen={modalIsOpen}
                handleClose={() => setModalOpen(false)}
            />
        </section>
    );
};

export default basePageWrap(ObjectPage);
